import { useTranslation } from "react-i18next";
import {
  useCreateAiAssessmentScale,
  useGetAiAssessmentScale,
  getAiAssessmentScale,
} from "@packages/api/madlenAPI";
import {
  AiAssessmentScaleResponseDto,
  AiAssessmentScaleCreateRequestDtoCourseName,
  BaseTeacherToolResponseDtoStatus,
} from "@packages/api/madlenAPI.schemas";
import GenericToolCreationPage from "../../../components/tool_components/GenericToolCreationPage";
import {
  AiAssessmentScaleCreateModal,
  CreateModalProps,
} from "@features/tools/components/create-modal/modals";
import AiAssessmentScaleCard from "@features/tools/components/ai-assessment-scale/AiAssessmentScaleCard";
import { useEffect } from "react";

const CreateAiAssessmentScaleView = () => {
  const { t } = useTranslation();

  const CreateModalWrapper = ({
    initialInput,
    onSubmit,
    isLoading,
    error,
  }: Partial<CreateModalProps>) => (
    <AiAssessmentScaleCreateModal
      title={t("aiAssessmentScale.createButton")}
      backPath={`/content/ai-assessment-scale`}
      loadingTitle={t("aiAssessmentScale.loading_description")}
      errorTitle={t("aiAssessmentScale.error.title")}
      errorDescription={t("aiAssessmentScale.error.description")}
      submitButtonText={t("common.generate")}
      initialInput={initialInput!}
      onSubmit={async (input) => {
        await onSubmit!(input);
      }}
      isLoading={isLoading!}
      error={error!}
    />
  );

  const ToolCardWrapper = ({
    toolData,
    isImageLoading,
    onCreateAgain,
  }: {
    toolData: AiAssessmentScaleResponseDto;
    isImageLoading?: boolean;
    onCreateAgain: () => void;
  }) => {
    return (
      <AiAssessmentScaleCard
        assessmentScale={toolData}
        isImageLoading={isImageLoading}
        onCreateAgain={onCreateAgain}
      />
    );
  };

  return (
    <GenericToolCreationPage
      toolType="ai-assessment-scale"
      useCreateTool={useCreateAiAssessmentScale}
      useGetTool={useGetAiAssessmentScale}
      getTool={getAiAssessmentScale}
      ToolCard={ToolCardWrapper}
      CreateModalWrapper={CreateModalWrapper}
      toolDataExtractor={(data: AiAssessmentScaleResponseDto) => {
        if (!data) return null;

        // Check for FAILED status
        if (data.status === BaseTeacherToolResponseDtoStatus.FAILED) {
          console.error("AI Assessment Scale generation failed");
          return null;
        }

        // More comprehensive check for data presence
        const hasLevels = Array.isArray(data.levels) && data.levels.length > 0;

        // Lambda might return table_title and title in different fields from what frontend expects
        // Check if the raw response contains these fields
        let hasDataInAnyForm = hasLevels;

        // If we have actual content, consider it ready regardless of status
        if (hasDataInAnyForm) {
          return true;
        }

        // If still in PROCESSING or PENDING with no content, continue polling
        return null;
      }}
    />
  );
};

export default CreateAiAssessmentScaleView;
